import { Component, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '../app.service';
import { StorageService } from '../shared/storage.service';
import { UtilsService } from '../shared/utils.service';
import { DefaultAccountComponent } from '../default-account/default-account.component';
import { ApiRequestService } from '../shared/api-request.service';
import { UserMessagesService } from '../user/user-messages/user-messages.service';
import { AppcuesService } from '../services/appcues.service';
import { SentryService } from '../services/sentry.service';
import { AuthService } from '../shared/auth.service';
import { VerifyEmailRequiredDialogComponent } from './verify-email-required-dialog/verify-email-required-dialog.component';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class SignInComponent implements OnInit, OnDestroy {
  email: string;
  password: string;
  passwordFieldType: string = 'password';

  // Which tab is active: 0 = Sign In, 1 = Sign Up.
  selectedTabIndex: number = 0;

  private redirect = 'dashboard';

  inProgress: boolean = false;

  // Wait before each automatic retry of the page load token validation after a transport failure.
  private static readonly AUTO_VALIDATE_RETRY_DELAYS_MS: number[] = [1000, 3000, 6000];
  // Wait after the browser reports it is back online, so the first request does not straddle the network change.
  private static readonly ONLINE_RETRY_DELAY_MS: number = 2000;
  private autoValidateRetryTimer?: ReturnType<typeof setTimeout>;
  // True while the page load validation failed to reach the server and may still be retried.
  private awaitingRetry: boolean = false;
  constructor(
    private activatedRoute: ActivatedRoute,
    private utils: UtilsService,
    private app: AppService,
    private storage: StorageService,
    private api: ApiRequestService,
    private userMessagesService: UserMessagesService,
    private appcuesService: AppcuesService,
    private sentry: SentryService,
    private authService: AuthService
  ) {}

  ngOnInit() {
    // Get the redirect route param.
    this.redirect = this.activatedRoute.snapshot.params['redirect'];
    // Check for an alternative redirect query param.
    if (!this.redirect) {
      this.redirect = this.activatedRoute.snapshot.queryParams['redirect'];
    }
    // Automatically sign the user in if the user did not sign out previously.
    this.autoValidateUserAuthentication();
  }

  /**
   * Automatically validate the user's authentication if the user did not sign out.
   * The user's auth token is stored in the local storage.
   *
   * The stored token is only dropped when the API answers that it is dead (401, or 403 for a blocked
   * account). A failure to reach the API says nothing about the token, so it is kept, the validation
   * is retried a few times, and the user is otherwise left on the normal sign-in form.
   *
   * @param attempt The number of retries already made after a transport failure.
   */
  autoValidateUserAuthentication(attempt: number = 0) {
    // Get the auth token from the local storage.
    const apiToken = this.storage.get('AuthToken');
    // Terminate early if the auth token is not set.
    if (!apiToken) {
      return;
    }
    // Terminate early if another sign-in process is in progress.
    if (this.inProgress) {
      return;
    }
    // Set it as in progress.
    this.inProgress = true;
    // Make the API request to validate the user's auth token.
    this.api
      // Flagged as a background request so the error interceptor stays quiet during the retries and
      // the user gets at most one message, from the final failure below.
      .makeRequest('post', 'v2/api-token-validation', {
        api_token: apiToken
      }, {}, {}, false, true)
      .then(
        (response): void => {
          this.inProgress = false;
          this.awaitingRetry = false;
          this.handleTokenResponse(response);
        }
      )
      .catch(
        (errors): void => {
          this.inProgress = false;

          // An unverified user holding a stored token lands here. Their token is not invalid, so the
          // generic error plus "please sign in again" is both wrong and a dead end: signing in again
          // produces the same refusal. Route them into the verification flow instead.
          //
          // Only the stored token is dropped, rather than calling authService.signOut(). The auto
          // sign-in failed, so no user or account state was ever applied, and signOut() would add a
          // "You were signed out" toast plus a redirect to the page the user is already on. Dropping the
          // token is what actually matters: it stops the auto sign-in retrying on every reload.
          if ( this.handleVerificationRequired(errors) ) {
            this.storage.delete('AuthToken');
            return;
          }

          // A 401 here means the stored token is dead, which is expected rather than a fault: every
          // token issued before the hashed token rollout stops authenticating, and tokens also expire
          // or get revoked. Showing the "Error Message" modal for it reads like a product failure on
          // the user's first visit, so it is handled quietly with a single friendly message.
          if ( errors?.status === 401 ) {
            const signInAgainMessage: string = this.utils.getLangTerm(
              'auth.sign_in_again',
              'Please sign in again to continue.'
            );
            if ( this.app.isAuthenticated ) {
              // The session was live and a request was rejected mid-session (the interceptor sent the
              // user here). Application state is populated, so do a full sign out. That clears the
              // token and state and shows one toast, which carries the message.
              this.authService.signOut(signInAgainMessage);
            } else {
              // First load: no user or account state was applied, so only the dead token is dropped.
              // No "You were signed out" toast and no redirect, since the user is already here.
              this.storage.delete('AuthToken');
              this.utils.showToast(signInAgainMessage);
            }
            return;
          }

          // Only a 403 (for example a blocked account) is an answer from the API about this user, so it
          // behaves as it always has.
          if ( errors?.status === 403 ) {
            this.awaitingRetry = false;
            this.utils.handleAPIErrors(errors);
            // Sign the user out if the API token is invalid.
            this.authService.signOut(
              'Please sign in again to continue using the application.'
            );
            return;
          }

          // A failure to reach the server (status 0) or a rate limit (429) says nothing about the token.
          // Keep the token and local storage, and try again shortly.
          if ( errors?.status === 0 || errors?.status === 429 ) {
            this.awaitingRetry = true;
            const delays: number[] = SignInComponent.AUTO_VALIDATE_RETRY_DELAYS_MS;
            if ( attempt < delays.length ) {
              this.scheduleAutoValidate(delays[attempt], attempt + 1);
            } else {
              // Retries exhausted: one message through the single owner of the connectivity message. The
              // token stays and the user is left on the normal sign-in form. The browser's online event
              // starts a fresh attempt.
              this.utils.handleAPIErrors(errors);
            }
            return;
          }

          // A 5xx or anything unexpected: show the message only. It says nothing about the token, so
          // there is no sign out and local storage is left alone.
          this.awaitingRetry = false;
          this.utils.handleAPIErrors(errors);
        }
      );
  }

  /**
   * Run the page load token validation again after a delay, unless the user has signed in meanwhile.
   *
   * @param delayMs How long to wait before the attempt.
   * @param attempt The retry count to pass on to autoValidateUserAuthentication().
   */
  private scheduleAutoValidate(delayMs: number, attempt: number): void {
    clearTimeout(this.autoValidateRetryTimer);
    this.autoValidateRetryTimer = setTimeout((): void => {
      if ( this.app.isAuthenticated || !this.storage.get('AuthToken') ) {
        return;
      }
      this.autoValidateUserAuthentication(attempt);
    }, delayMs);
  }

  /**
   * The browser regained the network. If the page load validation failed to reach the server, try
   * again after a short settle delay.
   */
  @HostListener('window:online')
  onOnline(): void {
    if ( !this.awaitingRetry || this.inProgress ) {
      return;
    }
    this.scheduleAutoValidate(SignInComponent.ONLINE_RETRY_DELAY_MS, 0);
  }

  ngOnDestroy(): void {
    clearTimeout(this.autoValidateRetryTimer);
  }

  /**
   * The user has submitted the sign-in form.
   * @param form The NgForm object.
   */
  onSubmit(form: NgForm) {
    // Validate the form inputs.
    //
    // Titled for what the user needs to do, not as an error. Nothing has gone wrong when somebody has
    // simply not finished filling the form in, and "Error Message" on a sign-in screen reads like a
    // fault in the product rather than a prompt.
    if (!form.valid) {
      this.utils.showModal(
        'Check Your Details',
        'Please make sure you enter all required fields with valid formats.'
      );
      return;
    }
    // Check if another sign-in process is in progress.
    if (this.inProgress) {
      return;
    }
    // Set it as in progress.
    this.inProgress = true;
    // Make the API request to authenticate the user.
    this.api
      .makeRequest('post', 'v2/login', {
        email: this.email,
        password: this.password
      })
      .then((response) => {
        this.inProgress = false;
        this.handleTokenResponse(response);
      })
      .catch((errors) => {
        this.inProgress = false;

        // An unverified email address is not a failure the user can fix by trying again, so it gets its
        // own dialog with a way forward instead of the generic error modal.
        if ( this.handleVerificationRequired(errors) ) {
          return;
        }

        this.utils.handleAPIErrors(errors);
      });
  }

  /**
   * Show the email verification dialog when the API refused the sign-in for that reason.
   *
   * Returns true when the error was handled here, so callers can skip their normal error reporting. The
   * API signals this with verification_required on a 403 (see AuthService::handleAuthResponse), which is
   * matched on rather than the message text so the wording can change freely.
   *
   * @param errors The rejected response from the sign-in or token validation request.
   * @return True when the verification dialog was shown and the caller should stop.
   */
  private handleVerificationRequired(errors: any): boolean {
    const body: any = errors ? errors.error : null;

    if ( !body || !body.verification_required ) {
      return false;
    }

    // Prefer the address the API reported, since it is the account's actual email. The typed value is
    // the fallback, and may be a username rather than an email address.
    this.utils.showComponentDialog(
      VerifyEmailRequiredDialogComponent,
      {
        email: body.email || this.email || '',
        message: body.message || ''
      },
      { width: '600px' }
    );

    return true;
  }

  /**
   * This is used to handle the user's sign-in response.
   * Similar method defined in reset-password.component.ts
   * @param response The response object from the API.
   * @private
   */
  private handleTokenResponse(response: any): void {
    // Check if we have an account and user in the response data.
    if (!response.account || !response.user) {
      this.utils.showToast(
        'Your account or user is invalid. Please try again.'
      );
      this.inProgress = false;
      return;
    }

    // Update auth details.
    this.authService.updateAccountAuthDetails(response);

    // Set the in-progress status to false.
    this.inProgress = false;

    // Get a copy of the query params that were preserved.
    let redirect_params: any = JSON.parse(
      JSON.stringify(this.activatedRoute.snapshot.queryParams)
    );
    if (typeof redirect_params['redirect'] !== 'undefined') {
      // Remove redirect from the preserved query params.
      delete redirect_params['redirect'];
    }

    // SC-301: resolve any outstanding membership request BEFORE redirecting.
    //
    // A user waiting on approval is signed in but cannot see the account they asked to join. Landing them
    // on an empty dashboard makes that look like a fault in the product, so they go to the page that
    // explains it instead. Doing this before the redirect avoids bouncing them through the dashboard first.
    this.api.makeRequest('get', 'v2/user/account-access-requests')
      .then((requestsResponse: any): void => {
        const requests: any[] = requestsResponse?.data || [];
        this.completeSignIn(redirect_params, requests);
      })
      .catch((): void => {
        // Never block sign in on this. A failed lookup just means normal routing.
        this.completeSignIn(redirect_params, []);
      });
  }

  /**
   * Performs the post-sign-in redirect, diverting to the membership status page where appropriate.
   *
   * @param redirect_params The preserved query params.
   * @param requests The user's outstanding and refused membership requests.
   */
  private completeSignIn(redirect_params: any, requests: any[]): void {
    const hasPending: boolean = requests.some(
      (row: any): boolean => row.approval_status === 'pending'
    );

    // Divert only when the user has nothing else to land on: an outstanding request AND an active account
    // that is their own personal one. Somebody already approved elsewhere keeps their normal landing page
    // even with a second request outstanding, and an explicit deep link always wins.
    const isOnPersonalAccount: boolean =
      !!this.app.account?.personal_user_id &&
      this.app.account.personal_user_id === this.app.user?.id;

    const divertToStatusPage: boolean =
      requests.length > 0 && isOnPersonalAccount && !this.redirect;

    const target: string[] = divertToStatusPage
      ? ['/user/account-access']
      : this.redirect
        ? [this.redirect]
        : ['/dashboard'];

    this.utils
      .redirectPage(target, divertToStatusPage ? {} : redirect_params)
      .then(
        (): void => {
          // The yearly employer validation prompt must not stack on top of the pending state. It asks the
          // user to confirm an employer they have already asked to join and cannot change until somebody
          // approves it, so it would be an unanswerable question in an undismissable dialog.
          if (!hasPending) {
            this.checkDefaultAccount();
          }
          // Get the user's messages meta. This includes the number of unread messages.
          this.userMessagesService.getMessagesMeta();
        }
      );
  }

  /**
   * Displays a dialog to the user if the default account needs to be updated.
   */
  private checkDefaultAccount() {
    // Check if the user has updated their default account in the last year.
    if (!this.shouldUpdateAccount(this.app.user.account_id_updated_at)) {
      return;
    }
    // Show a message to the user to validate their default employer.
    this.utils.showToast('Please validate your default employer.');
    // Show the default account selection dialog.
    this.utils.showComponentDialog(
      DefaultAccountComponent,
      {},
      { width: '500px' }
    );
  }

  /**
   * Does the users default account need updating?
   *
   * @param updatedAt Unix timestamp of when the account was last set.
   */
  private shouldUpdateAccount(updatedAt: number): boolean {
    // Return true if the user never updated their default account.
    if (!updatedAt) {
      return true;
    }
    // Check if the user updated their default account in the last year.
    const now = new Date().getTime();
    const milliseconds = now - updatedAt * 1000;
    const years = milliseconds / 1000 / 60 / 60 / 24 / 30.41 / 12;
    return years >= 1;
  }

  /**
   * Called when the sign-up form reports a successful registration. Switch back to the Sign In tab so
   * the user can sign in after verifying their email.
   */
  onSignedUp(): void {
    this.selectedTabIndex = 0;
  }

  /**
   * Toggles the visibility of the password field.
   *
   * @returns {void}
   */
  togglePasswordVisibility(): void {
    this.passwordFieldType =
      this.passwordFieldType === 'password' ? 'text' : 'password';
  }
}
